import { useCallback, useEffect, useState } from 'react'
import { api, ApiRoutes } from '../services/ApiService'
import * as yup from 'yup'
import { AnyObject } from 'yup'

export const useStoreData = <DataType extends object | unknown[]>(
    url: ApiRoutes,
    validation: AnyObject,
    initialValue?: DataType
) => {
    const [element, setElement] = useState<DataType>(initialValue ?? (Array.isArray(initialValue) ? ([] as unknown as DataType) : ({} as DataType)))
    const [files, setFiles] = useState<File[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [isValid, setIsValid] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)

    const changeProp = useCallback((data: Partial<DataType extends object ? DataType : {}>) => {
        setElement((prevElement) => {
            if (!prevElement) return data as DataType

            if (typeof prevElement === 'object' && !Array.isArray(prevElement) && typeof data === 'object' && !Array.isArray(data)) {
                return { ...(prevElement as object), ...(data as object) } as DataType
            }

            return data as DataType
        })
    }, [])

    const updateArrayItem = useCallback(<T>(
        array: T[],
        index: number,
        newItem: T
    ) => {
        if (!array) return array
        return array.map((item, i) => (i === index ? newItem : item))
    }, [])

    const addFile = (file: File) => {
        setFiles((prevFiles) => [...prevFiles, file])
    }

    const changeFile = (file: File) => {
        setFiles([file])
    }

    const clearData = () => {
        setFiles([])
        setElement(initialValue ?? (Array.isArray(initialValue) ? ([] as unknown as DataType) : ({} as DataType)))
    }

    useEffect(() => {
        if (Array.isArray(element)) {
            validation.validate(element)
                .then(() => {
                    setError(null)
                    setIsValid(true)
                })
                .catch((reason:unknown) => {
                    console.error(reason)
                    setError(reason instanceof Error ? reason.message : String(reason))
                    setIsValid(false)
                })
        } else {
            yup.object(validation).validate(element)
                .then(() => {
                    setError(null)
                    setIsValid(true)
                })
                .catch((reason) => {
                    setError(reason)
                    setIsValid(false)
                })
        }
    }, [element, validation])

    const submit = async () => {
        setIsLoading(true)

        const response = await api.post(url, undefined, element, files)
        setIsLoading(false)

        return response
    }

    return {
        error,
        element,
        isValid,
        isLoading,
        files,
        changeProp,
        updateArrayItem,
        clearData,
        addFile,
        changeFile,
        submit,
    }
}
